// Typed fetch client for /api routes. Thin wrapper over fetch with JSON.
// Throws ApiError on non-2xx. Expects CF Access cookies to travel with the request.

import type {
  AgentsCostResponse,
  CreatePortfolioInput,
  CreateRepresentedInput,
  CreditsResponse,
  MatchScoreResult,
  MeResponse,
  OutreachDraft,
  OverviewResponse,
  PortfolioDetailResponse,
  PortfolioListResponse,
  PricingResponse,
  RepresentedDetailResponse,
  RepresentedListResponse,
  RLPDetailResponse,
  RLPListResponse,
  RLPRequirements,
  WorkspacesResponse,
} from './api-types';
import { getActiveWorkspaceId } from './workspace';

export class ApiError extends Error {
  constructor(readonly status: number, readonly detail: string) {
    super(`api ${status}: ${detail}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const wsId = getActiveWorkspaceId();
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(wsId ? { 'X-Workspace-Id': wsId } : {}),
      ...init?.headers,
    },
  });
  const text = await res.text();
  if (!res.ok) {
    throw new ApiError(res.status, text || res.statusText);
  }
  return text ? (JSON.parse(text) as T) : ({} as T);
}

export const api = {
  overview: () => request<OverviewResponse>('/api/overview'),

  listRlps: (
    params: { status?: string; sort?: string; q?: string; limit?: number; offset?: number } = {}
  ) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== '') qs.set(k, String(v));
    }
    return request<RLPListResponse>(`/api/rlps${qs.size ? `?${qs}` : ''}`);
  },

  getRlp: (id: string) => request<RLPDetailResponse>(`/api/rlps/${encodeURIComponent(id)}`),

  patchRequirements: (id: string, body: Partial<RLPRequirements>) =>
    request<{ requirements: RLPRequirements }>(
      `/api/rlps/${encodeURIComponent(id)}/requirements`,
      { method: 'PATCH', body: JSON.stringify(body) }
    ),

  listPendingDrafts: () => request<{ drafts: OutreachDraft[] }>('/api/outreach/pending'),

  listThreadDrafts: () => request<{ drafts: OutreachDraft[] }>('/api/outreach/threads'),

  approveDraft: (id: string, body: { subject?: string; body?: string } = {}) =>
    request<{ draft: OutreachDraft }>(`/api/outreach/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  rejectDraft: (id: string, reason: string) =>
    request<{ draft: OutreachDraft }>(`/api/outreach/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  agentsCost: (month?: string) =>
    request<AgentsCostResponse>(`/api/agents/cost${month ? `?month=${encodeURIComponent(month)}` : ''}`),

  pauseStatus: () => request<{ paused: boolean }>('/api/agents/pause-status'),

  pauseAgents: () => request<{ paused: true }>('/api/agents/pause', { method: 'POST' }),

  resumeAgents: () => request<{ paused: false }>('/api/agents/resume', { method: 'POST' }),

  me: () => request<MeResponse>('/api/me'),

  listWorkspaces: () => request<WorkspacesResponse>('/api/workspaces'),

  credits: (limit?: number) =>
    request<CreditsResponse>(`/api/credits${limit ? `?limit=${limit}` : ''}`),

  creditsPricing: () => request<PricingResponse>('/api/credits/pricing'),

  extractRlp: (rlpId: string) =>
    request<{
      rlp_id: string;
      extraction_status: 'pending';
      spent_credits: number;
      billing_exempt: boolean;
      transaction_id: string;
    }>(`/api/rlps/${encodeURIComponent(rlpId)}/extract`, { method: 'POST', body: '{}' }),

  listPortfolio: () => request<PortfolioListResponse>('/api/portfolio'),

  getPortfolioProperty: (id: string) =>
    request<PortfolioDetailResponse>(`/api/portfolio/${encodeURIComponent(id)}`),

  createPortfolioProperty: (body: CreatePortfolioInput) =>
    request<{ id: string }>('/api/portfolio', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  uploadPortfolioDocument: async (
    propertyId: string,
    file: File,
    docType: string
  ): Promise<{ id: string; r2_key: string; original_filename: string; doc_type: string; bytes: number }> => {
    // Multipart bypasses the JSON request() helper so the browser can set the
    // boundary header itself. Workspace header is forwarded the same way.
    const wsId = getActiveWorkspaceId();
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    const res = await fetch(`/api/portfolio/${encodeURIComponent(propertyId)}/documents`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(wsId ? { 'X-Workspace-Id': wsId } : {}),
      },
      body: fd,
    });
    const text = await res.text();
    if (!res.ok) throw new ApiError(res.status, text || res.statusText);
    return JSON.parse(text);
  },

  listRepresented: () => request<RepresentedListResponse>('/api/represented'),

  getRepresentedProperty: (id: string) =>
    request<RepresentedDetailResponse>(`/api/represented/${encodeURIComponent(id)}`),

  createRepresentedProperty: (body: CreateRepresentedInput) =>
    request<{ id: string }>('/api/represented', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  uploadRepresentedDocument: async (
    propertyId: string,
    file: File,
    docType: string
  ): Promise<{ id: string; r2_key: string; original_filename: string; doc_type: string; bytes: number }> => {
    const wsId = getActiveWorkspaceId();
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    const res = await fetch(`/api/represented/${encodeURIComponent(propertyId)}/documents`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(wsId ? { 'X-Workspace-Id': wsId } : {}),
      },
      body: fd,
    });
    const text = await res.text();
    if (!res.ok) throw new ApiError(res.status, text || res.statusText);
    return JSON.parse(text);
  },

  matchRlp: (rlpId: string) =>
    request<{
      rlp_id: string;
      spent_credits: number;
      billing_exempt: boolean;
      transaction_id: string;
    }>(`/api/rlps/${encodeURIComponent(rlpId)}/match`, { method: 'POST', body: '{}' }),

  scorePortfolioMatch: (propertyId: string, rlpId: string) =>
    request<MatchScoreResult>(
      `/api/portfolio/${encodeURIComponent(propertyId)}/score`,
      { method: 'POST', body: JSON.stringify({ rlp_id: rlpId }) }
    ),

  costTimeseries: (params: { granularity?: 'hour' | 'day'; days?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.granularity) qs.set('granularity', params.granularity);
    if (params.days != null) qs.set('days', String(params.days));
    return request<{
      granularity: 'hour' | 'day';
      since_ms: number;
      buckets: { t: number; agent: string; cost_usd: number; runs: number }[];
    }>(`/api/agents/cost-timeseries${qs.size ? `?${qs}` : ''}`);
  },
};
